import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";

import { ActionButtonIntent, HeaderDivider, TriggerButton, TriggerButtonIntent } from "widgets";

import { type ISelectionModel } from "../../app-utils/selection-model.js";
import { pluralizeWord } from "../../app-utils/string.js";
import { type ParentType } from "../../graphql.js";
import { clearParentTypeSelection, deleteParentTypes } from "../../redux/modules/sample-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { ConfirmationDialog } from "../core/overlays/Dialogs.js";
import { ModalActionWidget } from "../core/overlays/index.js";
import AddParentTypeDialog from "./AddParentTypeDialog.js";

interface IParentTypesAdminToolbarOwnProps {
  renderOffScreen?: boolean; // Only useful for off-screen rendering
}

interface IParentTypesAdminToolbarAppStateProps {
  // Redux injected props
  parentTypes: ParentType[];
  selectionModel: ISelectionModel;
}

interface IParentTypesAdminToolbarDispatchProps {
  deleteParentType: (parentTypeIds: number[]) => Promise<boolean>;
  clearParentTypeSelection: () => void;
}

export interface IParentTypesAdminToolbarProps
  extends IParentTypesAdminToolbarAppStateProps,
    IParentTypesAdminToolbarDispatchProps,
    IParentTypesAdminToolbarOwnProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: IParentTypesAdminToolbarOwnProps,
): IParentTypesAdminToolbarAppStateProps {
  return {
    parentTypes: state.sampleAdmin.parentTypes,
    selectionModel: state.sampleAdmin.parentTypesSelectionModel,
  };
}

function mapDispatchToProps(
  dispatch,
  _ownProps: IParentTypesAdminToolbarOwnProps,
): IParentTypesAdminToolbarDispatchProps {
  return {
    deleteParentType: (parentTypeIds: number[]) => dispatch(deleteParentTypes(parentTypeIds)),
    clearParentTypeSelection: () => dispatch(clearParentTypeSelection()),
  };
}

interface IParentTypesAdminToolbarState {
  showAddParentType?: boolean;
}

const INIT_STATE: IParentTypesAdminToolbarState = {
  showAddParentType: false,
};

class ParentTypesAdminToolbarImpl extends PureComponent<IParentTypesAdminToolbarProps, IParentTypesAdminToolbarState> {
  constructor(props: IParentTypesAdminToolbarProps) {
    super(props);
    this.state = INIT_STATE;
  }

  getDeleteButtonDisabledTooltip = () => {
    if (this.props.selectionModel.selectedIds.length === 0) {
      return "At least one parent type must be selected to perform the deletion";
    } else if (
      this.props.selectionModel.selectedIds.find((selectedId: number) => {
        const selectedParentType: ParentType = this.props.parentTypes.find(
          (parentType: ParentType) => parentType.id === selectedId,
        );
        return selectedParentType && selectedParentType.sampleCount > 0;
      })
    ) {
      return "A parent type can only be deleted if there are no samples associated with the parent type";
    } else if (
      this.props.selectionModel.selectedIds.find((selectedId: number) => {
        const selectedParentType: ParentType = this.props.parentTypes.find(
          (parentType: ParentType) => parentType.id === selectedId,
        );
        return selectedParentType && selectedParentType.sampleTypeCount > 0;
      })
    ) {
      return "A parent type can only be deleted if there are no sample types associated with the parent type";
    }
  };

  handleDeleteParentType = () => {
    return this.props.deleteParentType(this.props.selectionModel.selectedIds as number[]).then(() => {
      this.props.clearParentTypeSelection();
    });
  };

  render(): ReactElement {
    const deleteButtonDisabledTooltip: string = this.getDeleteButtonDisabledTooltip();

    return (
      <>
        <ModalActionWidget
          actionWidget={({ open }) => (
            <TriggerButton
              id={"parent-type-admin-delete"}
              tooltip={"Delete the selected parent type(s)"}
              onPress={() => open()}
              isDisabled={deleteButtonDisabledTooltip}
              intent={TriggerButtonIntent.Danger}
              icon={"delete"}
              // renderOffScreen={this.props.renderOffScreen}
              label={"Delete"}
              showLabel
            />
          )}
        >
          {({ close }) => (
            <ConfirmationDialog
              headerProps={{
                title: `Delete Parent ${pluralizeWord("Type", this.props.selectionModel.selectedIds.length)}`,
              }}
              mainActionButtonProps={{
                children: `Delete`,
                onPress: () => this.handleDeleteParentType().then(() => close()),
                intent: ActionButtonIntent.Danger,
              }}
              closeFn={close}
            >
              Are you sure you want to delete {this.props.selectionModel.selectedIds.length} selected Parent{" "}
              {pluralizeWord("Type", this.props.selectionModel.selectedIds.length)}?
            </ConfirmationDialog>
          )}
        </ModalActionWidget>
        <HeaderDivider />
        <TriggerButton
          key={"sample-type-admin-add"}
          icon={"add"}
          tooltip={"Create a new parent type"}
          onPress={() => this.setState({ showAddParentType: true })}
          label={"New Parent Type"}
          showLabel
        />

        {this.state.showAddParentType && this.props.renderOffScreen !== true && (
          <AddParentTypeDialog
            title={"Add New Parent Type"}
            onApply={() => this.setState({ showAddParentType: false })}
            onClose={() => this.setState({ showAddParentType: false })}
          />
        )}
      </>
    );
  }
}

const ParentTypesAdminToolbar = connect<
  IParentTypesAdminToolbarAppStateProps,
  IParentTypesAdminToolbarDispatchProps,
  IParentTypesAdminToolbarOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(ParentTypesAdminToolbarImpl);

export { ParentTypesAdminToolbar };
export default ParentTypesAdminToolbar;
